import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { getGameDetails } from '../../apiservices/LocalStorageService';
import '../gamelobby/HostSketcherPlay.css';
import Confetti from '../animations/Confetti';

const podiumColors = ['#D4A843', '#8B7355', '#CD7F32'];

interface Props {
  leaderboard: LeaderboardEntry[];
  yourPlayerId?: string;
}

const PlayerSketcherGameOver: React.FC<Props> = ({ leaderboard, yourPlayerId }) => {
  const navigate = useNavigate();
  const gamedetail = getGameDetails();
  const { gameTitle } = gamedetail || {};

  const you = leaderboard?.find((p) => p.playerId === yourPlayerId);
  const yourRank = leaderboard?.findIndex((p) => p.playerId === yourPlayerId) + 1;
  const top3 = (leaderboard || []).slice(0, 3);
  // Podium display order: 2nd, 1st, 3rd
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
  const podiumPlaces = top3.length >= 3 ? [2, 1, 3] : top3.map((_, i) => i + 1);

  return (
    <div className="sketcher-over">
      <Confetti active />
      <button
        className="sketcher-over__back"
        onClick={() => navigate('/join', { state: { clearOldGame: true } })}
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
      </button>

      <div className="sketcher-over__content">
        {/* Header */}
        <div className="sketcher-over__header">
          <span className="sketcher-over__trophy">
            <FontAwesomeIcon icon={faTrophy} />
          </span>
          <h1 className="sketcher-over__title">Game Over!</h1>
          <p className="sketcher-over__subtitle">
            {gameTitle || 'Sketcher Game'}
          </p>
        </div>

        {/* Your result highlight */}
        {you && (
          <div className="sketcher-over__your-result">
            <span className="sketcher-over__your-rank">#{yourRank}</span>
            <span className="sketcher-over__your-name">{you.playerName}</span>
            <span className="sketcher-over__your-score">{you.totalPoints} pts</span>
          </div>
        )}

        {/* Podium */}
        {top3.length > 0 && (
          <div className="sketcher-over__podium">
            {podiumOrder.map((player, i) => {
              const place = podiumPlaces[i];
              return (
                <div key={player.playerId} className="sketcher-over__podium-col">
                  <span
                    className="sketcher-over__podium-avatar"
                    style={{ background: player.iconColor }}
                  >
                    {player.playerIcon}
                  </span>
                  <span className="sketcher-over__podium-name">{player.playerName}</span>
                  <div
                    className={`sketcher-over__podium-block sketcher-over__podium-block--${place}`}
                    style={{ background: podiumColors[i] }}
                  >
                    <span className="sketcher-over__podium-place">{place}</span>
                    <span className="sketcher-over__podium-score">{player.totalPoints} pts</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full leaderboard */}
        {leaderboard?.length > 0 && (
          <div className="sketcher-over__leaderboard">
            <h3 className="sketcher-over__lb-title">Final Scoreboard</h3>
            <div className="sketcher-over__lb-list">
              {leaderboard.map((player) => (
                <div
                  key={player.playerId}
                  className={`sketcher-over__lb-row ${player.playerId === yourPlayerId ? 'sketcher-over__lb-row--you' : ''}`}
                >
                  <span className="sketcher-over__lb-rank">#{player.rank}</span>
                  <span
                    className="sketcher-over__lb-avatar"
                    style={{ background: player.iconColor }}
                  >
                    {player.playerIcon}
                  </span>
                  <div className="sketcher-over__lb-info">
                    <span className="sketcher-over__lb-name">
                      {player.playerName}
                      {player.playerId === yourPlayerId && ' (You)'}
                    </span>
                  </div>
                  <span className="sketcher-over__lb-score">{player.totalPoints} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No leaderboard fallback */}
        {(!leaderboard || leaderboard.length === 0) && (
          <div className="sketcher-over__leaderboard">
            <p className="sketcher-over__lb-title">Results are being tallied...</p>
          </div>
        )}

        {/* Actions */}
        <div className="sketcher-over__actions">
          <button
            className="sketcher-over__btn sketcher-over__btn--primary"
            onClick={() => navigate('/join', { state: { clearOldGame: true } })}
          >
            Play Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerSketcherGameOver;
